/**
 * Device Detection Utility
 * 
 * Detects device type for multi-device workflow optimization.
 * Used to show appropriate actions (scan on mobile, edit on desktop).
 * 
 * Categories:
 * - mobile: phones (best for quick scans, limited editing)
 * - tablet: iPads, Android tablets (ideal for scanning)
 * - desktop: laptops, desktops (ideal for content editing)
 */

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface DeviceInfo {
  type: DeviceType;
  isTouchDevice: boolean;
  hasCamera: boolean;
  screenWidth: number;
  isOptimalForScanning: boolean;
  isOptimalForEditing: boolean;
}

/**
 * Detect current device type based on user agent and screen size.
 * Uses a combination of signals for accuracy.
 */
export function detectDeviceType(): DeviceType {
  if (typeof window === 'undefined') {
    return 'desktop'; // SSR fallback
  }

  const ua = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  
  // Check for tablets first (iPad, Android tablets)
  const isTablet = 
    /ipad/.test(ua) ||
    (/macintosh/.test(ua) && 'ontouchend' in document) || // iPad with desktop UA
    (/android/.test(ua) && !/mobile/.test(ua)) ||
    (screenWidth >= 768 && screenWidth <= 1024 && 'ontouchend' in document);
  
  if (isTablet) {
    return 'tablet';
  }
  
  // Check for mobile phones
  const isMobile = 
    /iphone|ipod|android.*mobile|windows phone|blackberry|opera mini|iemobile/.test(ua) ||
    (screenWidth < 768 && 'ontouchend' in document);
  
  if (isMobile) {
    return 'mobile';
  }
  
  return 'desktop';
}

/**
 * Get comprehensive device info for UI decisions.
 */
export function getDeviceInfo(): DeviceInfo {
  const type = detectDeviceType();
  const isTouchDevice = typeof window !== 'undefined' && 'ontouchend' in document;
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  
  // Check camera availability (async, but we provide sync estimate)
  const hasCamera = typeof navigator !== 'undefined' && 
    'mediaDevices' in navigator && 
    'getUserMedia' in navigator.mediaDevices;
  
  return {
    type,
    isTouchDevice,
    hasCamera,
    screenWidth,
    // Tablets are ideal for scanning (good camera, portable, stable)
    // Mobile works but is less stable
    isOptimalForScanning: type === 'tablet' || type === 'mobile',
    // Desktop is ideal for editing (keyboard, large screen)
    // Tablet is acceptable but not optimal
    isOptimalForEditing: type === 'desktop',
  };
}

/**
 * Get human-readable device name for UI display.
 */
export function getDeviceDisplayName(type: DeviceType): string {
  switch (type) {
    case 'mobile':
      return 'Phone';
    case 'tablet':
      return 'iPad/Tablet';
    case 'desktop':
      return 'Computer';
  }
}

/**
 * Get workflow recommendation based on current device and task status.
 */
export function getWorkflowRecommendation(
  currentDevice: DeviceType,
  taskStatus: 'assigned' | 'scanning' | 'editing'
): {
  primaryAction: 'scan' | 'edit' | 'wait';
  message: string;
  showDeviceHint: boolean;
} {
  if (taskStatus === 'assigned' || taskStatus === 'scanning') {
    // Task needs scanning
    if (currentDevice === 'tablet') {
      return {
        primaryAction: 'scan',
        message: 'Ready to scan pages',
        showDeviceHint: false,
      };
    } else if (currentDevice === 'mobile') {
      return {
        primaryAction: 'scan',
        message: 'Ready to scan (tablet recommended for best results)',
        showDeviceHint: true,
      };
    } else {
      return {
        primaryAction: 'scan',
        message: 'For best results, scan on your iPad or tablet',
        showDeviceHint: true,
      };
    }
  } else if (taskStatus === 'editing') {
    // Task needs editing
    if (currentDevice === 'desktop') {
      return {
        primaryAction: 'edit',
        message: 'Ready to edit content',
        showDeviceHint: false,
      };
    } else {
      return {
        primaryAction: 'edit',
        message: 'For easier editing, switch to your computer',
        showDeviceHint: true,
      };
    }
  }
  
  return {
    primaryAction: 'wait',
    message: 'Processing...',
    showDeviceHint: false,
  };
}

/**
 * React hook for device detection with resize handling.
 */
export function useDeviceType(): DeviceInfo {
  // This is a simple sync implementation.
  // For a more robust solution, use useState + useEffect with resize listener.
  return getDeviceInfo();
}
